<!-- Initialization Loading State -->
<div class="init-loading-state" *ngIf="isInitializing">
  <div class="init-loading-content">
    <div class="init-loading-spinner"></div>
    <span>Loading Analytics...</span>
  </div>
</div>

<div class="no-org-state" *ngIf="!isInitializing && organizations.length === 0">
  <div class="no-org-content">
    <i class="fa-solid fa-building-circle-xmark fa-3x"></i>
    <h2>No Organizations</h2>
    <p>You are not a member of any organization. Contact your administrator to get access.</p>
  </div>
</div>

<div class="analytics-container" [class.nav-collapsed]="isNavCollapsed" *ngIf="!isInitializing && organizations.length > 0">
  <!-- Left Navigation Sidebar -->
  <nav class="analytics-nav">
    <div class="nav-header">
      <div class="nav-title" *ngIf="!isNavCollapsed">
        <i class="fa-solid fa-chart-line"></i>
        <span>Analytics</span>
      </div>
      <button class="collapse-btn" (click)="toggleNav()" [title]="isNavCollapsed ? 'Expand' : 'Collapse'">
        <i class="fa-solid" [ngClass]="isNavCollapsed ? 'fa-angles-right' : 'fa-angles-left'"></i>
      </button>
    </div>


    <!-- Navigation Items -->
    <div class="nav-section">
      <div class="section-label" *ngIf="!isNavCollapsed">Views</div>
      <div class="nav-items">
        <button
          *ngFor="let section of sections"
          class="nav-item"
          [class.active]="selectedSection.id === section.id"
          (click)="selectSection(section)"
          [title]="isNavCollapsed ? section.label : ''"
        >
          <i class="fa-solid {{ section.icon }}"></i>
          <span class="nav-label" *ngIf="!isNavCollapsed">{{ section.label }}</span>
        </button>
      </div>
    </div>

    <!-- Spacer -->
    <div class="nav-spacer"></div>

    <!-- Export Section -->
    <div class="nav-section nav-footer" *ngIf="!isNavCollapsed">
      <button class="export-btn" (click)="exportToCSV()" title="Export to CSV">
        <i class="fa-solid fa-download"></i>
        <span>Export CSV</span>
      </button>
    </div>
    <div class="nav-section nav-footer" *ngIf="isNavCollapsed">
      <button class="nav-item" (click)="exportToCSV()" title="Export to CSV">
        <i class="fa-solid fa-download"></i>
      </button>
    </div>
  </nav>

  <!-- Main Content Area -->
  <main class="analytics-content">
    <!-- Content Header -->
    <div class="content-header">
      <div class="header-info">
        <i class="fa-solid {{ selectedSection.icon }}"></i>
        <div class="header-text">
          <h1>{{ selectedSection.label }}</h1>
          <p>{{ selectedSection.description }}</p>
        </div>
      </div>
      <div class="header-controls">
        <!-- Org selection comes from the global header switcher -->
        <!-- Date Range Picker -->
        <div class="date-range-picker">
          <button class="date-range-trigger" (click)="toggleDatePicker()">
            <i class="fa-regular fa-calendar"></i>
            <span>{{ dateRangeLabel }}</span>
            <i class="fa-solid fa-chevron-down date-range-chevron" [class.open]="showDatePicker"></i>
          </button>
          <div class="date-range-dropdown" *ngIf="showDatePicker">
            <div class="date-range-presets">
              <button
                *ngFor="let range of timeRanges"
                [class.active]="selectedTimeRange === range.value"
                (click)="onTimeRangeChange(range.value)"
              >{{ range.label }}</button>
            </div>
            <div class="date-range-divider"></div>
            <div class="date-range-custom">
              <label>Custom Range</label>
              <div class="date-range-inputs">
                <input type="date" [(ngModel)]="customStartDate" (change)="onCustomDateChange()" [max]="customEndDate || ''" />
                <span class="date-range-separator">to</span>
                <input type="date" [(ngModel)]="customEndDate" (change)="onCustomDateChange()" [min]="customStartDate || ''" />
              </div>
            </div>
          </div>
          <div class="date-range-backdrop" *ngIf="showDatePicker" (click)="closeDatePicker()"></div>
        </div>

        <!-- Refresh Button -->
        <button class="refresh-btn" (click)="refresh()" [disabled]="isRefreshing">
          <i class="fa-solid" [ngClass]="isRefreshing ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-state" *ngIf="isLoading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>Loading analytics...</span>
    </div>

    <!-- Content Body -->
    <div class="content-body" *ngIf="!isLoading">

      <!-- ==================== OVERVIEW SECTION ==================== -->
      <div class="section-content overview-section" *ngIf="selectedSection.id === 'overview'">
        <!-- Fixed height content: KPI Cards and Charts -->
        <div class="overview-fixed-content">
          <!-- KPI Cards Row -->
          <div class="kpi-row">
          <div
            class="kpi-card clickable"
            *ngFor="let kpi of kpiCards"
            [style.--accent-color]="kpi.color"
            (click)="onKPIClick(kpi)"
          >
            <div class="kpi-icon">
              <i class="fa-solid {{ kpi.icon }}"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-value">{{ kpi.value }}</span>
              <span class="kpi-title">{{ kpi.title }}</span>
              <div class="kpi-meta">
                <span [class]="kpi.changeIsPercent ? 'kpi-change pill' : 'kpi-change plain'" *ngIf="kpi.change !== undefined || kpi.changeLabel">
                  <ng-container *ngIf="kpi.change !== undefined">{{ kpi.change }}{{ kpi.changeIsPercent ? '%' : '' }} </ng-container>{{ kpi.changeLabel }}
                </span>
                <span class="kpi-trend" *ngIf="kpi.trend" [class.up]="kpi.trend === 'up'" [class.down]="kpi.trend === 'down'">
                  <i class="fa-solid" [ngClass]="kpi.trend === 'up' ? 'fa-arrow-up' : kpi.trend === 'down' ? 'fa-arrow-down' : 'fa-minus'"></i>
                  {{ kpi.trendPercent }}% vs prev
                </span>
              </div>
            </div>
            <div class="kpi-drill-hint">
              <i class="fa-solid fa-chevron-right"></i>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="charts-row">
          <!-- Message Volume Chart -->
          <div class="chart-card volume-chart">
            <div class="chart-header">
              <h3><i class="fa-solid fa-chart-line"></i> Message Volume & Response Time</h3>
              <div class="chart-legend">
                <span
                  class="legend-item total"
                  [class.active]="chartSeries.total"
                  (click)="onToggleChartSeries('total')"
                >
                  <span class="legend-checkbox">
                    <i class="fa-solid" [ngClass]="chartSeries.total ? 'fa-check-square' : 'fa-square'"></i>
                  </span>
                  <span class="legend-dot"></span>
                  Total
                </span>
                <span
                  class="legend-item approved"
                  [class.active]="chartSeries.approved"
                  (click)="onToggleChartSeries('approved')"
                >
                  <span class="legend-checkbox">
                    <i class="fa-solid" [ngClass]="chartSeries.approved ? 'fa-check-square' : 'fa-square'"></i>
                  </span>
                  <span class="legend-dot"></span>
                  Approved
                </span>
                <span
                  class="legend-item response-time"
                  [class.active]="chartSeries.responseTime"
                  (click)="onToggleChartSeries('responseTime')"
                >
                  <span class="legend-checkbox">
                    <i class="fa-solid" [ngClass]="chartSeries.responseTime ? 'fa-check-square' : 'fa-square'"></i>
                  </span>
                  <span class="legend-dot"></span>
                  Avg Response
                </span>
              </div>
            </div>
            <div class="chart-body">
              <div class="d3-line-chart-container" #lineChartContainer *ngIf="messageVolumeTrend.length > 0"></div>
              <div class="empty-chart" *ngIf="messageVolumeTrend.length === 0">
                <i class="fa-solid fa-chart-line"></i>
                <p>No message data available</p>
              </div>
            </div>
          </div>

          <!-- Status Breakdown -->
          <div class="chart-card status-chart">
            <div class="chart-header">
              <h3><i class="fa-solid fa-pie-chart"></i> Generation Status</h3>
            </div>
            <div class="chart-body">
              <div class="status-breakdown" *ngIf="generationStatusBreakdown.length > 0">
                <div class="status-bars">
                  <div
                    class="status-bar-item clickable"
                    *ngFor="let item of generationStatusBreakdown"
                    (click)="onGenerationStatusClick(item)"
                  >
                    <div class="status-info">
                      <span class="status-dot" [style.background]="item.color"></span>
                      <span class="status-name">{{ item.status }}</span>
                      <span class="status-count">{{ item.count }}</span>
                    </div>
                    <div class="status-bar-track">
                      <div class="status-bar-fill" [style.width.%]="item.percentage" [style.background]="item.color"></div>
                    </div>
                    <span class="status-percentage">{{ item.displayPercentage }}</span>
                    <i class="fa-solid fa-chevron-right drill-hint"></i>
                  </div>
                </div>
              </div>
              <div class="empty-chart" *ngIf="generationStatusBreakdown.length === 0">
                <i class="fa-solid fa-pie-chart"></i>
                <p>No status data available</p>
              </div>
            </div>
          </div>

          <!-- Approval Status -->
          <div class="chart-card approval-chart">
            <div class="chart-header">
              <h3><i class="fa-solid fa-check-circle"></i> Approval Status</h3>
            </div>
            <div class="chart-body">
              <div class="status-breakdown" *ngIf="approvalStatusBreakdown.length > 0">
                <div class="status-bars">
                  <div
                    class="status-bar-item clickable"
                    *ngFor="let item of approvalStatusBreakdown"
                    (click)="onApprovalStatusClick(item)"
                  >
                    <div class="status-info">
                      <span class="status-dot" [style.background]="item.color"></span>
                      <span class="status-name">{{ item.status }}</span>
                      <span class="status-count">{{ item.count }}</span>
                    </div>
                    <div class="status-bar-track">
                      <div class="status-bar-fill" [style.width.%]="item.percentage" [style.background]="item.color"></div>
                    </div>
                    <span class="status-percentage">{{ item.displayPercentage }}</span>
                    <i class="fa-solid fa-chevron-right drill-hint"></i>
                  </div>
                </div>
              </div>
              <div class="empty-chart" *ngIf="approvalStatusBreakdown.length === 0">
                <i class="fa-solid fa-check-circle"></i>
                <p>No approval data available</p>
              </div>
            </div>
          </div>
        </div>
        </div><!-- end overview-fixed-content -->

        <!-- Recent Activity Row - fills remaining height -->
        <div class="activity-row">
          <!-- Recent Messages -->
          <div class="activity-card">
            <div class="activity-header">
              <h3><i class="fa-solid fa-envelope"></i> Recent Messages</h3>
            </div>
            <div class="activity-body">
              <div class="activity-list" *ngIf="recentMessages.length > 0">
                <div
                  class="activity-item clickable"
                  *ngFor="let msg of recentMessages"
                  (click)="navigateToMessage(msg)"
                >
                  <div class="activity-icon" [style.background]="getStatusColor(msg.GenerationStatus)">
                    <i class="fa-solid fa-envelope"></i>
                  </div>
                  <div class="activity-content">
                    <div class="activity-title">{{ truncateText(msg.Subject || msg.Sender, 40) }}</div>
                    <div class="activity-meta">
                      <span class="status-badge" [style.background]="getStatusColor(msg.GenerationStatus)">
                        {{ msg.GenerationStatus }}
                      </span>
                      <span class="channel-tag">{{ getChannelName(msg.ChannelID) }}</span>
                      <span class="time">{{ getTimeSince(msg.ReceivedAt) }}</span>
                    </div>
                  </div>
                  <div class="activity-nav-hint">
                    <i class="fa-solid fa-external-link-alt"></i>
                  </div>
                </div>
              </div>
              <div class="empty-activity" *ngIf="recentMessages.length === 0">
                <i class="fa-solid fa-inbox"></i>
                <p>No recent messages</p>
              </div>
            </div>
          </div>

          <!-- Recent Channel Runs -->
          <div class="activity-card">
            <div class="activity-header">
              <h3><i class="fa-solid fa-sync"></i> Recent Channel Runs</h3>
            </div>
            <div class="activity-body">
              <div class="activity-list" *ngIf="recentRuns.length > 0">
                <div class="activity-item" *ngFor="let run of recentRuns">
                  <div class="activity-icon" [style.background]="getStatusColor(run.Status)">
                    <i class="fa-solid fa-sync"></i>
                  </div>
                  <div class="activity-content">
                    <div class="activity-title">{{ getChannelName(run.ChannelID) }}</div>
                    <div class="activity-meta">
                      <span class="status-badge" [style.background]="getStatusColor(run.Status)">
                        {{ run.Status }}
                      </span>
                      <span class="run-stats">{{ run.ProcessedCount || 0 }} processed</span>
                      <span class="time">{{ getTimeSince(run.StartedAt) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="empty-activity" *ngIf="recentRuns.length === 0">
                <i class="fa-solid fa-history"></i>
                <p>No recent channel runs</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== PERFORMANCE SECTION ==================== -->
      <div class="section-content" *ngIf="selectedSection.id === 'performance'">
        <!-- SLA Tracking Card -->
        <div class="performance-grid">
          <div class="chart-card sla-card" *ngIf="slaSettings.enabled">
            <div class="chart-header">
              <h3><i class="fa-solid fa-bullseye"></i> SLA Performance</h3>
              <div class="sla-target-info">
                <span class="sla-target-label">Target:</span>
                <span class="sla-target-value">{{ formatDuration(slaSettings.targetMinutes) }}</span>
              </div>
            </div>
            <div class="chart-body sla-body">
              <div class="sla-gauge">
                <div class="gauge-circle" [style.--percent]="slaMetPercent">
                  <div class="gauge-inner">
                    <span class="gauge-value">{{ slaMetPercent }}%</span>
                    <span class="gauge-label">Meeting SLA</span>
                  </div>
                </div>
              </div>
              <div class="sla-stats">
                <div class="sla-stat met">
                  <i class="fa-solid fa-check-circle"></i>
                  <span class="stat-value">{{ slaMetCount }}</span>
                  <span class="stat-label">Within {{ formatDuration(slaSettings.targetMinutes) }}</span>
                </div>
                <div class="sla-stat missed">
                  <i class="fa-solid fa-times-circle"></i>
                  <span class="stat-value">{{ slaMissedCount }}</span>
                  <span class="stat-label">Exceeded</span>
                </div>
              </div>
            </div>
          </div>
          <div class="chart-card sla-disabled-card" *ngIf="!slaSettings.enabled">
            <div class="chart-header">
              <h3><i class="fa-solid fa-bullseye"></i> SLA Performance</h3>
            </div>
            <div class="chart-body sla-disabled-body">
              <i class="fa-solid fa-toggle-off"></i>
              <p>SLA tracking is disabled for this organization</p>
              <span class="sla-hint">Enable it in Administration &gt; Settings &gt; SLA</span>
            </div>
          </div>

          <!-- Response Time Stats -->
          <div class="chart-card response-stats-card">
            <div class="chart-header">
              <h3><i class="fa-solid fa-stopwatch"></i> Response Time Stats</h3>
            </div>
            <div class="chart-body">
              <div class="response-time-stats">
                <div class="stat-item">
                  <span class="stat-label">Average</span>
                  <span class="stat-value primary">{{ formatDuration(avgResponseTimeMinutes) }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Minimum</span>
                  <span class="stat-value success">{{ formatDuration(minResponseTimeMinutes) }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Maximum</span>
                  <span class="stat-value warning">{{ formatDuration(maxResponseTimeMinutes) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Channel Comparison -->
        <div class="chart-card full-width">
          <div class="chart-header">
            <h3><i class="fa-solid fa-code-compare"></i> Channel Comparison</h3>
          </div>
          <div class="chart-body">
            <div class="channel-comparison-container" #channelComparisonContainer *ngIf="channelPerformance.length > 0"></div>
            <div class="empty-chart" *ngIf="channelPerformance.length === 0">
              <i class="fa-solid fa-broadcast-tower"></i>
              <p>No channel data available</p>
            </div>
          </div>
        </div>

        <!-- Channel Performance Table -->
        <div class="table-card">
          <div class="table-header">
            <h3><i class="fa-solid fa-broadcast-tower"></i> Channel Performance Details</h3>
          </div>
          <div class="table-body">
            <table class="performance-table" *ngIf="channelPerformance.length > 0">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th class="num">Messages</th>
                  <th class="num">Generated</th>
                  <th class="num">Failed</th>
                  <th class="num">Avg Response</th>
                  <th class="num">Success Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let channel of channelPerformance"
                  class="clickable-row"
                  (click)="onChannelClick(channel)"
                >
                  <td class="channel-name">
                    <i class="fa-solid fa-broadcast-tower"></i>
                    {{ channel.channelName }}
                  </td>
                  <td class="num">{{ channel.totalMessages }}</td>
                  <td class="num success">{{ channel.generated }}</td>
                  <td class="num danger">{{ channel.failed }}</td>
                  <td class="num">{{ formatDuration(channel.avgResponseTime) }}</td>
                  <td class="num">
                    <div class="success-rate-bar">
                      <div class="rate-fill" [style.width.%]="channel.successRate"></div>
                      <span class="rate-text">{{ channel.successRate }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="empty-table" *ngIf="channelPerformance.length === 0">
              <i class="fa-solid fa-broadcast-tower"></i>
              <p>No channel data available</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== TICKETS SECTION (§13) ==================== -->
      <div class="section-content tickets-section" *ngIf="selectedSection.id === 'tickets'">
        <!-- KPI row -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-chart-1)"><i class="fa-solid fa-ticket"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.created }}</span><span class="kpi-title">Tickets Created</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-success)"><i class="fa-solid fa-circle-check"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.resolved }}</span><span class="kpi-title">Resolved</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-warning)"><i class="fa-solid fa-layer-group"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.backlog }}</span><span class="kpi-title">Open Backlog</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-info)"><i class="fa-solid fa-hourglass-half"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.avgResolutionHours !== null ? ticketKpis.avgResolutionHours + 'h' : '—' }}</span><span class="kpi-title">Avg Resolution</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-chart-2)"><i class="fa-solid fa-reply"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.avgFirstResponseHours !== null ? ticketKpis.avgFirstResponseHours + 'h' : '—' }}</span><span class="kpi-title">Avg First Response</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-identity)"><i class="fa-solid fa-bullseye"></i></div>
            <div class="kpi-content"><span class="kpi-value">{{ ticketKpis.slaCompliancePct !== null ? ticketKpis.slaCompliancePct + '%' : '—' }}</span><span class="kpi-title">SLA Compliance</span></div>
          </div>
        </div>

        <!-- SLA split + reopen -->
        <div class="performance-grid">
          <div class="chart-card">
            <div class="chart-header"><h3><i class="fa-solid fa-gauge-high"></i> SLA Outcomes</h3></div>
            <div class="chart-body sla-stats">
              <div class="sla-stat met"><i class="fa-solid fa-check-circle"></i><span class="stat-value">{{ slaSplit.met }}</span><span class="stat-label">Met</span></div>
              <div class="sla-stat" style="color: var(--izzy-warning)"><i class="fa-solid fa-triangle-exclamation"></i><span class="stat-value">{{ slaSplit.nearBreach }}</span><span class="stat-label">Near breach</span></div>
              <div class="sla-stat missed"><i class="fa-solid fa-times-circle"></i><span class="stat-value">{{ slaSplit.breached }}</span><span class="stat-label">Breached</span></div>
            </div>
          </div>
          <div class="chart-card">
            <div class="chart-header"><h3><i class="fa-solid fa-rotate-left"></i> Reopen Rate</h3></div>
            <div class="chart-body" style="display:flex;align-items:center;justify-content:center;">
              <span class="kpi-value" style="font-size:2.2rem;">{{ reopenRatePct !== null ? reopenRatePct + '%' : '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Breakdown charts -->
        <div class="performance-grid">
          <div class="chart-card">
            <div class="chart-header"><h3><i class="fa-solid fa-list-check"></i> Tickets by Status</h3></div>
            <div class="chart-body"><div #ticketStatusContainer></div></div>
          </div>
          <div class="chart-card">
            <div class="chart-header"><h3><i class="fa-solid fa-tags"></i> Tickets by Type</h3></div>
            <div class="chart-body"><div #ticketTypeContainer></div></div>
          </div>
          <div class="chart-card">
            <div class="chart-header"><h3><i class="fa-solid fa-satellite-dish"></i> Tickets by Channel</h3></div>
            <div class="chart-body"><div #ticketChannelContainer></div></div>
          </div>
        </div>

        <!-- Per-assignee ticket workload — total per assignee split into open/resolved.
             Clickable: opens the Tickets queue filtered to that assignee (mirrors message Team Workload). -->
        <div class="chart-card">
          <div class="chart-header">
            <h3><i class="fa-solid fa-user-check"></i> Tickets by Assignee</h3>
          </div>
          <div class="chart-body">
            <div class="workload-table" *ngIf="ticketAssignmentSummaries.length > 0">
              <div
                class="workload-row clickable"
                *ngFor="let summary of ticketAssignmentSummaries"
                (click)="onTicketAssigneeClick(summary)"
              >
                <div class="workload-avatar" [class.unassigned]="!summary.personId">
                  <span *ngIf="summary.personId">{{ getContactInitials(summary.personName) }}</span>
                  <i *ngIf="!summary.personId" class="fa-solid fa-user-slash"></i>
                </div>
                <div class="workload-info">
                  <div class="workload-name">{{ summary.personName }}</div>
                  <div class="workload-bar-container">
                    <div class="workload-bar"
                         [style.width.%]="maxTicketAssignmentCount > 0 ? (summary.count / maxTicketAssignmentCount) * 100 : 0"
                         [class.bar-assigned]="summary.personId"
                         [class.bar-unassigned]="!summary.personId">
                    </div>
                  </div>
                </div>
                <div class="workload-counts">
                  <span class="workload-total">{{ summary.count }}</span>
                  <div class="workload-badges">
                    <span class="workload-badge pending" *ngIf="summary.openCount > 0"
                          title="Open">{{ summary.openCount }} O</span>
                    <span class="workload-badge escalated" *ngIf="summary.resolvedCount > 0"
                          title="Resolved">{{ summary.resolvedCount }} R</span>
                  </div>
                </div>
                <div class="workload-chevron">
                  <i class="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            </div>
            <div class="empty-chart" *ngIf="ticketAssignmentSummaries.length === 0">
              <i class="fa-solid fa-user-check"></i>
              <p>No tickets in this period</p>
            </div>
          </div>
        </div>

        <!-- Deferred metrics note (honest about what's not yet available) -->
        <div class="tickets-deferred-note">
          <i class="fa-solid fa-circle-info"></i>
          CSAT (satisfaction) and AI-resolution metrics arrive with the customer survey + AI-resolution tracking.
        </div>
      </div>

      <!-- ==================== ACTIVITY SECTION ==================== -->
      <div class="section-content" *ngIf="selectedSection.id === 'activity'">
        <!-- Peak Hours Heatmap -->
        <div class="chart-card full-width">
          <div class="chart-header">
            <h3><i class="fa-solid fa-fire"></i> Peak Hours Heatmap</h3>
            <span class="chart-subtitle">Click a cell to see messages</span>
          </div>
          <div class="chart-body heatmap-body">
            <div class="heatmap-container" #heatmapContainer *ngIf="heatmapData.length > 0"></div>
            <div class="heatmap-legend">
              <span>Low</span>
              <div class="legend-gradient"></div>
              <span>High</span>
            </div>
          </div>
        </div>

        <!-- Top Senders -->
        <div class="activity-grid">
          <div class="chart-card">
            <div class="chart-header">
              <h3><i class="fa-solid fa-users"></i> Top Senders</h3>
            </div>
            <div class="chart-body">
              <div class="top-senders-list" *ngIf="topSenders.length > 0">
                <div
                  class="sender-item clickable"
                  *ngFor="let sender of topSenders; let i = index"
                  (click)="onSenderClick(sender)"
                >
                  <span class="sender-rank">{{ i + 1 }}</span>
                  <div class="sender-info">
                    <span class="sender-name">{{ truncateText(sender.sender, 30) }}</span>
                    <div class="sender-bar">
                      <div class="sender-bar-fill" [style.width.%]="sender.percentage"></div>
                    </div>
                  </div>
                  <span class="sender-count">{{ sender.count }}</span>
                  <i class="fa-solid fa-chevron-right drill-hint"></i>
                </div>
              </div>
              <div class="empty-chart" *ngIf="topSenders.length === 0">
                <i class="fa-solid fa-users"></i>
                <p>No sender data available</p>
              </div>
            </div>
          </div>

          <!-- Message Distribution by Day -->
          <div class="chart-card">
            <div class="chart-header">
              <h3><i class="fa-solid fa-calendar-week"></i> Messages by Day</h3>
            </div>
            <div class="chart-body">
              <div class="day-distribution" *ngIf="heatmapData.length > 0">
                <div class="day-bar" *ngFor="let day of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; let i = index">
                  <span class="day-label">{{ day }}</span>
                  <div class="day-bar-track">
                    <div
                      class="day-bar-fill"
                      [style.width.%]="getDayPercentage(i)"
                    ></div>
                  </div>
                  <span class="day-count">{{ getDayCount(i) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== FEEDBACK SECTION ==================== -->
      <div class="section-content" *ngIf="selectedSection.id === 'feedback'">

        <!-- Summary KPI Cards -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-success)">
              <i class="fa-solid fa-thumbs-up"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ feedbackTotalUp }}</div>
              <div class="kpi-title">Thumbs Up</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-error)">
              <i class="fa-solid fa-thumbs-down"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ feedbackTotalDown }}</div>
              <div class="kpi-title">Thumbs Down</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-chart-3)">
              <i class="fa-solid fa-star-half-stroke"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ feedbackTotalRated }}</div>
              <div class="kpi-title">Total Rated</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-warning)">
              <i class="fa-solid fa-percent"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ feedbackThumbsUpPercent }}%</div>
              <div class="kpi-title">Positive Rate</div>
            </div>
          </div>
        </div>

        <!-- Feedback by Channel Table + Trend Chart -->
        <div class="activity-grid">

          <!-- By Channel Breakdown -->
          <div class="chart-card">
            <div class="chart-header">
              <h3><i class="fa-solid fa-broadcast-tower"></i> Feedback by Channel</h3>
            </div>
            <div class="chart-body table-body">
              <table class="performance-table" *ngIf="feedbackByChannel.length > 0">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th class="num"><i class="fa-solid fa-thumbs-up"></i></th>
                    <th class="num"><i class="fa-solid fa-thumbs-down"></i></th>
                    <th class="num">Positive</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let ch of feedbackByChannel">
                    <td class="channel-name">
                      <i class="fa-solid fa-broadcast-tower"></i>
                      {{ ch.channelName }}
                    </td>
                    <td class="num success">{{ ch.thumbsUp }}</td>
                    <td class="num danger">{{ ch.thumbsDown }}</td>
                    <td class="num">
                      <div class="success-rate-bar">
                        <div class="rate-fill" [style.width.%]="ch.thumbsUpPercent"
                             [style.background]="ch.thumbsUpPercent >= 70 ? '#22c55e' : ch.thumbsUpPercent >= 40 ? '#f59e0b' : '#ef4444'"></div>
                        <span class="rate-text">{{ ch.thumbsUpPercent }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="empty-table" *ngIf="feedbackByChannel.length === 0">
                <i class="fa-solid fa-thumbs-up"></i>
                <p>No feedback data available</p>
              </div>
            </div>
          </div>

          <!-- Daily Trend -->
          <div class="chart-card">
            <div class="chart-header">
              <h3><i class="fa-solid fa-chart-bar"></i> Daily Feedback Trend</h3>
            </div>
            <div class="chart-body">
              <div class="feedback-trend" *ngIf="feedbackTrend.length > 0">
                <div class="trend-y-axis">
                  <span class="trend-y-label">{{ maxFeedbackTrendValue }}</span>
                  <span class="trend-y-label">{{ feedbackTrendMidpoint }}</span>
                  <span class="trend-y-label">0</span>
                </div>
                <div class="trend-bars">
                  <div class="trend-bar-group" *ngFor="let point of feedbackTrend">
                    <div class="trend-stacked-bar">
                      <div class="trend-bar-up"
                           [style.height.%]="maxFeedbackTrendValue > 0 ? (point.thumbsUp / maxFeedbackTrendValue) * 100 : 0"
                           [title]="point.thumbsUp + ' thumbs up'"></div>
                      <div class="trend-bar-down"
                           [style.height.%]="maxFeedbackTrendValue > 0 ? (point.thumbsDown / maxFeedbackTrendValue) * 100 : 0"
                           [title]="point.thumbsDown + ' thumbs down'"></div>
                    </div>
                    <span class="trend-label">{{ point.label }}</span>
                  </div>
                </div>
              </div>
              <div class="empty-chart" *ngIf="feedbackTrend.length === 0 || maxFeedbackTrendValue === 0">
                <i class="fa-solid fa-chart-bar"></i>
                <p>No trend data available</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ==================== CATEGORIES SECTION ==================== -->
      <div class="section-content" *ngIf="selectedSection.id === 'categories'">

        <!-- Summary KPI Cards -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-chart-1)">
              <i class="fa-solid fa-tags"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ categoryBreakdown.length }}</div>
              <div class="kpi-title">Active Categories</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-success)">
              <i class="fa-solid fa-check-double"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ totalCategorized }}</div>
              <div class="kpi-title">Categorized</div>
            </div>
          </div>
        </div>

        <!-- Category Tiles -->
        <div class="category-tiles" *ngIf="categoryBreakdown.length > 0">
          <div
            class="category-tile"
            *ngFor="let item of categoryBreakdown; let i = index"
            (click)="onCategoryClick(item)"
            [style.--tile-color]="item.color"
           
            [class.category-tile-large]="i === 0 && categoryBreakdown.length > 2"
          >
            <div class="tile-top">
              <div class="tile-count">{{ item.count }}</div>
              <div class="tile-pct-ring">
                <svg viewBox="0 0 36 36">
                  <path class="tile-ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="tile-ring-fill" [style.stroke]="item.color"
                    [attr.stroke-dasharray]="item.percentage + ', 100'"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span class="tile-pct-label">{{ item.displayPercentage }}</span>
              </div>
            </div>
            <div class="tile-name">{{ item.category }}</div>
            <div class="tile-bar">
              <div class="tile-bar-fill" [style.width.%]="item.percentage" [style.background]="item.color"></div>
            </div>
            <div class="tile-hint">
              <span>View messages</span>
              <i class="fa-solid fa-arrow-right"></i>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div class="chart-card" *ngIf="categoryBreakdown.length === 0">
          <div class="chart-body">
            <div class="empty-chart">
              <i class="fa-solid fa-tags"></i>
              <p>No category data available yet</p>
              <p style="font-size: 0.75rem; margin-top: 4px;">Messages will be categorized as they're processed</p>
            </div>
          </div>
        </div>

      </div>

      <!-- ==================== ASSIGNMENTS SECTION (Admin/Owner only) ==================== -->
      <div class="section-content assignments-section" *ngIf="selectedSection.id === 'assignments' && isCurrentUserAdmin">

        <!-- KPI Cards Row -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-chart-3)">
              <i class="fa-solid fa-inbox"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ totalActiveMessages }}</div>
              <div class="kpi-title">Active Messages</div>
              <div class="kpi-meta">
                <span class="kpi-change">Pending + Escalated</span>
              </div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-success)">
              <i class="fa-solid fa-user-check"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ totalAssigned }}</div>
              <div class="kpi-title">Assigned</div>
              <div class="kpi-meta">
                <span class="kpi-change" *ngIf="totalActiveMessages > 0">
                  {{ totalActiveMessages > 0 ? ((totalAssigned / totalActiveMessages) * 100 | number:'1.0-0') : 0 }}% of active
                </span>
              </div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon" style="background: var(--izzy-warning)">
              <i class="fa-solid fa-user-slash"></i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ totalUnassigned }}</div>
              <div class="kpi-title">Unassigned</div>
              <div class="kpi-meta">
                <span class="kpi-change" *ngIf="totalActiveMessages > 0">
                  {{ totalActiveMessages > 0 ? ((totalUnassigned / totalActiveMessages) * 100 | number:'1.0-0') : 0 }}% of active
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Team Workload: Open = current pending/escalated queue per assignee (the original
             view); Handled = approvals in the period per approver + the Izzy row (AI-handled). -->
        <div class="chart-card">
          <div class="chart-header">
            <h3><i class="fa-solid fa-users"></i> Team Workload</h3>
            <div class="workload-mode-toggle" role="tablist" aria-label="Workload view">
              <button type="button" role="tab" [attr.aria-selected]="workloadMode === 'open'"
                      [class.active]="workloadMode === 'open'" (click)="setWorkloadMode('open')">Open</button>
              <button type="button" role="tab" [attr.aria-selected]="workloadMode === 'handled'"
                      [class.active]="workloadMode === 'handled'" (click)="setWorkloadMode('handled')">Handled</button>
            </div>
          </div>
          <div class="chart-body">
            <div class="workload-table" *ngIf="assignmentSummaries.length > 0">
              <div
                class="workload-row clickable"
                *ngFor="let summary of assignmentSummaries"
                (click)="onAssignmentClick(summary)"
              >
                <div class="workload-avatar" [class.unassigned]="!summary.contactId && !summary.isIzzy" [class.izzy]="summary.isIzzy">
                  @if (summary.contactId) {
                    <span>{{ getContactInitials(summary.contactName) }}</span>
                  } @else if (summary.isIzzy) {
                    <i class="fa-solid fa-robot"></i>
                  } @else {
                    <i class="fa-solid fa-user-slash"></i>
                  }
                </div>
                <div class="workload-info">
                  <div class="workload-name">{{ summary.contactName }}</div>
                  <div class="workload-bar-container">
                    <div class="workload-bar"
                         [style.width.%]="maxAssignmentCount > 0 ? (summary.count / maxAssignmentCount) * 100 : 0"
                         [class.bar-assigned]="summary.contactId"
                         [class.bar-izzy]="summary.isIzzy"
                         [class.bar-unassigned]="!summary.contactId && !summary.isIzzy">
                    </div>
                  </div>
                </div>
                <div class="workload-counts">
                  <span class="workload-total">{{ summary.count }}</span>
                  <div class="workload-badges">
                    <span class="workload-badge pending" *ngIf="summary.pendingCount > 0"
                          title="Pending">{{ summary.pendingCount }} P</span>
                    <span class="workload-badge escalated" *ngIf="summary.escalatedCount > 0"
                          title="Escalated">{{ summary.escalatedCount }} E</span>
                    @if (summary.approvedCount > 0) {
                      <span class="workload-badge approved" title="Approved">{{ summary.approvedCount }} A</span>
                    }
                  </div>
                </div>
                <div class="workload-chevron">
                  <i class="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            </div>
            <div class="empty-chart" *ngIf="assignmentSummaries.length === 0">
              <i class="fa-solid fa-user-check"></i>
              <p>No active messages to display</p>
            </div>
          </div>
        </div>

      </div>

    </div>
  </main>
</div>

<!-- Drill-Down Modal -->
<div class="drill-down-overlay" *ngIf="showDrillDown" (click)="closeDrillDown()">
  <div class="drill-down-panel" (click)="$event.stopPropagation()">
    <div class="drill-down-header" *ngIf="drillDownFilter">
      <div class="drill-down-title">
        <div class="drill-down-icon" [style.background]="drillDownFilter.color">
          <i class="fa-solid {{ drillDownFilter.icon }}"></i>
        </div>
        <div class="drill-down-info">
          <h2>{{ drillDownFilter.title }}</h2>
          <span class="drill-down-subtitle">{{ drillDownFilter.value }}</span>
        </div>
      </div>
      <button class="close-btn" (click)="closeDrillDown()">
        <i class="fa-solid fa-times"></i>
      </button>
    </div>

    <div class="drill-down-summary">
      <div class="summary-stat">
        <span class="stat-value">{{ drillDownMessages.length }}</span>
        <span class="stat-label">Messages</span>
      </div>
    </div>

    <div class="drill-down-content">
      <div class="drill-down-empty" *ngIf="drillDownMessages.length === 0">
        <i class="fa-solid fa-inbox"></i>
        <p>No messages match this filter</p>
      </div>

      <div class="drill-down-list" *ngIf="drillDownMessages.length > 0">
        <div
          class="message-row clickable"
          *ngFor="let msg of drillDownMessages"
          (click)="navigateToMessage(msg)"
        >
          <div class="message-icon" [style.background]="getStatusColor(msg.GenerationStatus)">
            <i class="fa-solid fa-envelope"></i>
          </div>
          <div class="message-details">
            <div class="message-main">
              <span class="message-sender">{{ msg.Sender || 'Unknown Sender' }}</span>
              <span class="message-subject">{{ truncateText(msg.Subject || 'No Subject', 50) }}</span>
            </div>
            <div class="message-meta">
              <span class="badge" [style.background]="getStatusColor(msg.GenerationStatus)">
                {{ msg.GenerationStatus }}
              </span>
              <span class="badge" [style.background]="getStatusColor(msg.ApprovalStatus)">
                {{ msg.ApprovalStatus }}
              </span>
              <span class="channel-badge">
                <i class="fa-solid fa-broadcast-tower"></i>
                {{ getChannelName(msg.ChannelID) }}
              </span>
              <span class="time">{{ getTimeSince(msg.ReceivedAt) }}</span>
            </div>
          </div>
          <div class="message-nav-hint">
            <i class="fa-solid fa-external-link-alt"></i>
          </div>
        </div>
      </div>
    </div>

    <div class="drill-down-footer" *ngIf="drillDownMessages.length > 0">
      <span class="showing-count">Showing {{ drillDownMessages.length }} message(s)</span>
      <button class="close-action-btn" (click)="closeDrillDown()">
        <i class="fa-solid fa-times"></i> Close
      </button>
    </div>
  </div>
</div>
